import type { IntelData } from "./translate";
import { formatAge, freshnessFromTimestamp, isLiveSnapshotAgent } from "./dashboardDataContract";

export const DEFAULT_REALTIME_MARKET_WS_URL = "wss://oc-199134-e1jo.xc1.app/qst/realtime/market-flow";

const REALTIME_OVERLAY_KEYS = [
  "current_price",
  "current_price_source",
  "current_price_as_of",
  "current_price_age_sec",
  "current_price_status",
  "current_price_disagreement",
  "current_price_outlier_sources",
  "iceberg_bid",
  "iceberg_ask",
  "absorption_bid",
  "absorption_ask",
  "institutional_direction",
  "institutional_score",
  "spoof_bid_risk",
  "spoof_ask_risk",
  "order_flow_status",
  "order_flow_age_sec",
  "order_flow_source",
  "order_flow_method",
  "order_flow_as_of",
  "obv_divergence_10",
  "obv_divergence_20",
  "cmf_20",
  "mfi_14",
  "fbd_flow_score",
  "mr_flow_score",
  "volume_flow_status",
  "volume_flow_age_sec",
  "volume_flow_as_of",
  "volume_flow_source",
  "volume_flow_candle_seconds",
  "volume_flow_trade_count",
  "volume_flow_coverage_sec",
  "tape_accumulation_windows",
  "tape_accumulation_source",
  "tape_accumulation_as_of",
  "market_flow_stack",
] as const satisfies readonly (keyof IntelData)[];

const REALTIME_PRICE_KEYS = new Set<keyof IntelData>([
  "current_price",
  "current_price_source",
  "current_price_as_of",
  "current_price_age_sec",
  "current_price_status",
  "current_price_disagreement",
  "current_price_outlier_sources",
]);

const REALTIME_ORDER_FLOW_CLEAR_KEYS = [
  "iceberg_bid",
  "iceberg_ask",
  "absorption_bid",
  "absorption_ask",
  "institutional_direction",
  "institutional_score",
  "spoof_bid_risk",
  "spoof_ask_risk",
] as const satisfies readonly (keyof IntelData)[];

const REALTIME_VOLUME_FLOW_CLEAR_KEYS = [
  "obv_divergence_10",
  "obv_divergence_20",
  "cmf_20",
  "mfi_14",
  "fbd_flow_score",
  "mr_flow_score",
  "volume_flow_candle_seconds",
  "volume_flow_trade_count",
  "volume_flow_coverage_sec",
  "tape_accumulation_windows",
  "tape_accumulation_source",
  "tape_accumulation_as_of",
  "market_flow_stack",
] as const satisfies readonly (keyof IntelData)[];

type RealtimeOverlayKey = (typeof REALTIME_OVERLAY_KEYS)[number];
export type RealtimeMarketOverlay = Pick<Partial<IntelData>, RealtimeOverlayKey> & {
  symbol?: string;
};

export type RealtimeMarketStatus = "disabled" | "connecting" | "live" | "stale" | "error";

export interface RealtimeMarketMessage {
  type: "market_flow";
  transport: "websocket";
  mechanical: boolean;
  llm_calls: number;
  session: string;
  date: string;
  generated_at: string;
  order_flow_refresh_sec: number;
  volume_flow_refresh_sec: number;
  symbols: Record<string, RealtimeMarketOverlay>;
}

export function parseRealtimeMarketMessage(raw: string): RealtimeMarketMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== "object") return null;
  const msg = parsed as Partial<RealtimeMarketMessage>;
  if (msg.type !== "market_flow" || msg.transport !== "websocket") return null;
  if (msg.mechanical !== true || msg.llm_calls !== 0) return null;
  if (!msg.symbols || typeof msg.symbols !== "object") return null;
  return msg as RealtimeMarketMessage;
}

function dataStatusFromOverlay(liveFields: Partial<IntelData>): IntelData["data_status"] {
  const priceStatus = liveFields.current_price_status;
  if (priceStatus === "fresh") return "live";
  if (priceStatus === "stale") return "stale";
  if (priceStatus === "missing") return "missing";
  return typeof liveFields.current_price === "number" ? "live" : "missing";
}

function realtimeOverlayFields(liveFields: Partial<IntelData>, preserveBasePrice: boolean): Partial<IntelData> {
  const overlay: Partial<IntelData> = {};
  for (const key of REALTIME_OVERLAY_KEYS) {
    if (preserveBasePrice && REALTIME_PRICE_KEYS.has(key)) continue;
    const value = liveFields[key];
    if (value !== undefined) {
      (overlay as Record<string, unknown>)[key] = value;
    }
  }
  return overlay;
}

function realtimeClearFields(liveFields: Partial<IntelData>): Partial<IntelData> {
  const clears: Partial<IntelData> = {};
  if (liveFields.order_flow_status !== undefined) {
    for (const key of REALTIME_ORDER_FLOW_CLEAR_KEYS) {
      (clears as Record<string, unknown>)[key] = undefined;
    }
  }
  if (liveFields.volume_flow_status !== undefined) {
    for (const key of REALTIME_VOLUME_FLOW_CLEAR_KEYS) {
      (clears as Record<string, unknown>)[key] = undefined;
    }
  }
  return clears;
}

function realtimeFreshnessLabel(liveFields: Partial<IntelData>): string | undefined {
  if (typeof liveFields.current_price_age_sec === "number" && Number.isFinite(liveFields.current_price_age_sec)) {
    return formatAge(Math.max(0, liveFields.current_price_age_sec * 1000));
  }
  if (typeof liveFields.current_price_as_of === "number" && Number.isFinite(liveFields.current_price_as_of)) {
    return freshnessFromTimestamp(liveFields.current_price_as_of, Number.POSITIVE_INFINITY).label;
  }
  return undefined;
}

function hasRealtimePriceField(liveFields: Partial<IntelData>): boolean {
  for (const key of REALTIME_PRICE_KEYS) {
    if (liveFields[key] !== undefined) return true;
  }
  return false;
}

export function realtimeSymbolsForMerge<T extends Record<string, Partial<IntelData>>>(
  status: RealtimeMarketStatus,
  symbols: T | null,
): T | null {
  return status === "live" ? symbols : null;
}

export function mergeRealtimeMarketData(
  base: Record<string, IntelData>,
  realtime: Record<string, Partial<IntelData>> | null,
): Record<string, IntelData> {
  if (!realtime) return base;
  const merged: Record<string, IntelData> = { ...base };
  for (const [symbol, liveFields] of Object.entries(realtime)) {
    const baseRow = base[symbol] || { symbol };
    const isLiveSnapshot = isLiveSnapshotAgent(baseRow.agent);
    const overlayStatus = dataStatusFromOverlay(liveFields);
    const preserveBasePrice = hasRealtimePriceField(liveFields) && overlayStatus !== "live" && baseRow.data_status === "live";

    merged[symbol] = {
      ...baseRow,
      ...realtimeClearFields(liveFields),
      ...realtimeOverlayFields(liveFields, preserveBasePrice),
      symbol,
      // WebSocket payloads own live mechanical fields only. They should not
      // rewrite forecast direction/agent, signal levels, or separate
      // open-trade metadata chosen by the Convex snapshot pipeline.
      agent: baseRow.agent ?? "live",
      entry: isLiveSnapshot ? undefined : baseRow.entry,
      stop_loss: isLiveSnapshot ? undefined : baseRow.stop_loss,
      tp1: isLiveSnapshot ? undefined : baseRow.tp1,
      tp2: isLiveSnapshot ? undefined : baseRow.tp2,
      source_label: preserveBasePrice ? baseRow.source_label : "Live WebSocket",
      freshness_label: preserveBasePrice ? baseRow.freshness_label : realtimeFreshnessLabel(liveFields) ?? baseRow.freshness_label,
      data_status: preserveBasePrice ? baseRow.data_status : overlayStatus,
    } as IntelData;
  }
  return merged;
}

export function configuredRealtimeWsUrl(envUrl?: string): string | null {
  // Production must be realtime by default. Cloudflare/Pages can rebuild this
  // bundle without shell env injection, so the canonical public WSS URL lives
  // in source as a safe fallback. Pass an explicit blank string to disable.
  if (envUrl === undefined) return DEFAULT_REALTIME_MARKET_WS_URL;
  const trimmed = envUrl.trim();
  return trimmed.length > 0 ? trimmed : null;
}
